import React, { useEffect, useState } from 'react';
import { Download, X } from 'lucide-react';
import { phiText } from '../lib/phiConfig';

const SESSION_KEY = 'therapyops:sessionCount';
const DISMISSED_KEY = 'therapyops:installDismissed';
const MIN_SESSIONS = 3;

function bumpSessionCount() {
  try {
    const last = parseInt(sessionStorage.getItem('therapyops:sessionBumped') || '0', 10);
    if (last) return; // already bumped this tab session
    sessionStorage.setItem('therapyops:sessionBumped', '1');
    const prev = parseInt(localStorage.getItem(SESSION_KEY) || '0', 10);
    localStorage.setItem(SESSION_KEY, String(prev + 1));
  } catch (_) { /* ignore */ }
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    bumpSessionCount();
  }, []);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      try {
        const sessions = parseInt(localStorage.getItem(SESSION_KEY) || '0', 10);
        const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
        const longEnough = !dismissedAt || (Date.now() - dismissedAt) > 7 * 24 * 3600 * 1000;
        if (sessions >= MIN_SESSIONS && longEnough && !isStandalone()) {
          setVisible(true);
        }
      } catch (_) { /* ignore */ }
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!visible || !deferred) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice && choice.outcome === 'dismissed') dismiss();
      else setVisible(false);
    } catch (_) {
      setVisible(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-labelledby="install-title"
      data-testid="install-prompt"
      className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[360px] z-[120]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="rounded-2xl bg-white border border-[var(--color-border)] shadow-[0_18px_40px_rgba(15,42,74,0.18)] p-4 sm:p-5 flex items-start gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-[var(--gradient-brand)] flex items-center justify-center text-white" aria-hidden="true">
          <Download size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div id="install-title" className="font-display text-base font-semibold text-[var(--color-ink)] leading-tight">Install Operon</div>
          <p className="text-[12.5px] text-[var(--color-muted)] mt-1 leading-relaxed">Faster access and offline use — your data stays {phiText('HIPAA-secured', 'secured')}.</p>
          <div className="flex items-center gap-2 mt-3">
            <button
              type="button"
              onClick={install}
              className="inline-flex items-center justify-center min-h-[40px] px-3.5 rounded-xl bg-[var(--color-navy)] hover:bg-[var(--color-navy-deep)] active:scale-[0.98] text-white text-sm font-semibold transition-[transform,background-color] duration-150"
              data-testid="install-prompt-accept"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex items-center justify-center min-h-[40px] px-3 rounded-xl text-[var(--color-muted)] hover:text-[var(--color-ink)] hover:bg-[var(--color-surface-2)] text-sm font-medium transition-colors duration-150"
              data-testid="install-prompt-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="text-[var(--color-muted)] hover:text-[var(--color-ink)] -mr-1 -mt-1 p-2 rounded-lg hover:bg-[var(--color-surface-2)]"
          data-testid="install-prompt-close"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

export default InstallPrompt;
